// src/services/taskService.ts
// ─────────────────────────────────────────────────────────────────
// Task Service — Phase 3 (Mock)
//
// Phase 3 additions over Phase 1:
//   • getAllAssignableTasks()     — combines drawing/WI/AI/PM-created sources
//   • getTaskMaterials()         — BOM lines with stock status for a task
//   • calculateGoNoGo()          — derives GO/CONDITIONAL_GO/NO_GO
//   • approveAITask()            — PM approves DRAFT AI task for crew
//   • rejectAITask()             — PM rejects AI task
//   • recordNoGoOverride()       — stores override reason permanently
//   • recordStepCompletion()     — crew completes WI step; fires ITP notification
//
// At beta: replace function bodies with fetch('/api/tasks/...')
// ─────────────────────────────────────────────────────────────────

import {
  MOCK_CONFIGURED_TASKS,
  MOCK_MOBILE_TASK_RECORDS,
  MOCK_MOBILE_PHOTOS,
  type MobileTaskRecord,
  type MobilePhoto,
} from '../utils/mockDataMobile'
import {
  MOCK_DRAWING_TASKS,
  MOCK_WI_STEP_TASKS,
  MOCK_AI_TASKS,
  type DrawingTask,
  type WIStepTask,
  type AITask,
  type AITaskStatus,
} from '../utils/mockDataDrawingTasks'
import { MOCK_JOBS }                from '../utils/mockData'
import { addCrewNotification }      from '../utils/mockDataNotificationBus'
import type {
  AssignableTask, TaskMaterial, MaterialStatus, GoNoGoStatus, TaskSourceType, TaskPriority,
} from '../types/mobileCrewTypes'
import type { ITPReleasePayload }   from '../types/mobileCrewTypes'

// ── Local session stores ───────────────────────────────────────────
let _photoReviews: Record<string, { status: 'ACCEPTED' | 'FLAGGED_REWORK'; note?: string }> = {}
let _noGoOverrides: Record<string, string> = {}  // taskId → override reason
let _aiTaskStatuses: Record<string, AITaskStatus> = {}
let _aiApprovers: Record<string, { id: string; name: string; at: string }> = {}

// ── BOM status → MaterialStatus mapping ───────────────────────────
function bomStatusToMaterial(bomStatus: string): MaterialStatus {
  switch (bomStatus) {
    case 'DELIVERED':    return 'AVAILABLE_ON_SITE'
    case 'ON_SITE':      return 'AVAILABLE_ON_SITE'
    case 'WAREHOUSE':    return 'AVAILABLE_WAREHOUSE'
    case 'ON_ORDER':     return 'ON_ORDER_ETA'
    case 'NOT_ORDERED':  return 'NOT_ORDERED'
    default:             return 'NOT_ORDERED'
  }
}

/** Get materials for a task from the MOCK_JOBS BOM data. */
export async function getTaskMaterials(
  bomLineIds: string[],
  jobId: string,
): Promise<TaskMaterial[]> {
  // Phase 6: GET /api/tasks/materials?bomLineIds=...&jobId=...
  if (!bomLineIds.length) return []

  const job = (MOCK_JOBS as any[]).find(j => j.id === jobId)
  if (!job?.bomLines) return []

  const materials: TaskMaterial[] = bomLineIds
    .map(id => job.bomLines.find((b: any) => b.id === id))
    .filter(Boolean)
    .map((b: any) => ({
      bomLineId:    b.id,
      description:  b.description,
      quantity:     b.qty,
      unit:         b.unit,
      status:       bomStatusToMaterial(b.status) as MaterialStatus,
      poNumber:     b.poNumber ?? undefined,
    }))

  return Promise.resolve(materials)
}

/** Derive Go/No-Go status from a list of materials. */
export function calculateGoNoGo(materials: TaskMaterial[]): GoNoGoStatus {
  if (!materials.length) return 'NO_MATERIALS'

  const required = materials.filter(m => m.status !== 'NOT_REQUIRED')
  if (!required.length) return 'GO'

  const hasBlocker = required.some(m =>
    m.status === 'NOT_ORDERED' || m.status === 'ON_ORDER_ETA' || m.status === 'ON_ORDER_NO_ETA'
  )
  if (hasBlocker) return 'NO_GO'

  const hasConditional = required.some(m => m.status === 'AVAILABLE_WAREHOUSE')
  if (hasConditional) return 'CONDITIONAL_GO'

  return 'GO'
}

// ── Build AssignableTask from each source ──────────────────────────
async function fromDrawingTask(dt: DrawingTask): Promise<AssignableTask> {
  const mats = await getTaskMaterials(dt.bomLineIds, dt.jobId)
  const goNoGo = calculateGoNoGo(mats)
  return {
    taskId:         dt.id,
    taskSource:     'DRAWING',
    drawingRef:     `${dt.drawingNumber} ${dt.revision}`,
    drawingNumber:  dt.drawingNumber,
    title:          dt.title,
    description:    dt.description,
    specClauseRef:  dt.specClauseRef ?? undefined,
    discipline:     dt.discipline,
    priority:       'NORMAL',
    assignedToIds:  [],
    assignedToNames:[],
    jobId:          dt.jobId,
    jobNumber:      dt.jobNumber,
    targetDate:     new Date().toISOString().slice(0, 10),
    shiftType:      'DAY',
    materialsRequired: mats,
    goNoGoStatus:   goNoGo,
    noGoOverrideReason: _noGoOverrides[dt.id],
    photoRequired:  dt.photoRequired,
    photoCount:     dt.photoCount,
    holdPointLinkage: dt.holdPoint ?? undefined,
    pushedToMobile: false,
    approvalStatus: 'APPROVED',
  }
}

async function fromWIStepTask(wt: WIStepTask): Promise<AssignableTask> {
  const mats = await getTaskMaterials(wt.bomLineIds, wt.jobId)
  const goNoGo = calculateGoNoGo(mats)
  return {
    taskId:         wt.id,
    taskSource:     'WI_STEP',
    wiStepId:       wt.wiRef,
    wiStepTitle:    `${wt.wiRef} Step ${wt.stepNumber}`,
    title:          wt.stepTitle,
    description:    wt.stepDescription,
    discipline:     wt.discipline,
    priority:       wt.holdPointClass === 'HOLD' ? 'HIGH' : 'NORMAL',
    assignedToIds:  [],
    assignedToNames:[],
    jobId:          wt.jobId,
    jobNumber:      wt.jobNumber,
    targetDate:     new Date().toISOString().slice(0, 10),
    shiftType:      'DAY',
    materialsRequired: mats,
    goNoGoStatus:   goNoGo,
    noGoOverrideReason: _noGoOverrides[wt.id],
    photoRequired:  wt.photoRequired,
    photoCount:     wt.signOffRequired ? 1 : 0,
    holdPointLinkage: wt.holdPointRef ?? undefined,
    completionCriteria: wt.signOffRequired ? 'Crew must sign off in app to mark complete.' : undefined,
    pushedToMobile: false,
    approvalStatus: 'APPROVED',
  }
}

async function fromAITask(at: AITask): Promise<AssignableTask> {
  const status = (_aiTaskStatuses[at.id] ?? at.status) as AITaskStatus
  return {
    taskId:         at.id,
    taskSource:     'AI_TASK',
    aiTaskId:       at.aiJobRef,
    aiSourceDoc:    `${at.sourceDocRef} — ${at.sourceDocTitle}`,
    title:          at.title,
    description:    at.description,
    discipline:     at.discipline,
    priority:       'NORMAL',
    assignedToIds:  [],
    assignedToNames:[],
    jobId:          at.jobId,
    jobNumber:      at.jobNumber,
    targetDate:     new Date().toISOString().slice(0, 10),
    shiftType:      'DAY',
    materialsRequired: [],
    goNoGoStatus:   'NO_MATERIALS',
    noGoOverrideReason: _noGoOverrides[at.id],
    photoRequired:  true,
    photoCount:     2,
    pushedToMobile: false,
    approvalStatus: status === 'DRAFT' ? 'DRAFT' : status === 'APPROVED' ? 'APPROVED' : 'DRAFT',
  }
}

function fromConfiguredTask(ct: any): AssignableTask {
  return {
    taskId:          ct.id,
    taskSource:      ct.drawingTaskId ? 'DRAWING' : ct.workInstructionId ? 'WI_STEP' : 'PM_CREATED',
    drawingRef:      ct.drawingTaskId ?? undefined,
    wiStepId:        ct.workInstructionId ?? undefined,
    title:           ct.title,
    description:     ct.description,
    specClauseRef:   ct.specClauseRef ?? undefined,
    discipline:      ct.discipline,
    priority:        'NORMAL',
    assignedToIds:   ct.assignedToId ? [ct.assignedToId] : [],
    assignedToNames: ct.assignedToName ? [ct.assignedToName] : [],
    jobId:           ct.jobId,
    jobNumber:       ct.jobNumber,
    targetDate:      ct.targetDate,
    shiftType:       'DAY',
    materialsRequired:[],
    goNoGoStatus:    'NO_MATERIALS',
    noGoOverrideReason: _noGoOverrides[ct.id],
    photoRequired:   ct.photoRequirements > 0,
    photoCount:      ct.photoRequirements,
    holdPointLinkage:ct.holdPointLinkage ?? undefined,
    pushedToMobile:  ct.pushedToMobile,
    pushedAt:        ct.pushedAt ?? undefined,
    approvalStatus:  'APPROVED',
  }
}

// ── Public service functions ───────────────────────────────────────

/** All assignable tasks for a job, from all sources. */
export async function getAllAssignableTasks(jobId?: string): Promise<AssignableTask[]> {
  // Phase 6: GET /api/tasks/assignable?jobId=...
  const [drawingTasks, wiTasks, aiTasksList] = await Promise.all([
    Promise.all(
      MOCK_DRAWING_TASKS
        .filter(t => !jobId || t.jobId === jobId)
        .map(fromDrawingTask)
    ),
    Promise.all(
      MOCK_WI_STEP_TASKS
        .filter(t => !jobId || t.jobId === jobId)
        .map(fromWIStepTask)
    ),
    Promise.all(
      MOCK_AI_TASKS
        .filter(t => !jobId || t.jobId === jobId)
        .map(fromAITask)
    ),
  ])

  const configured = (MOCK_CONFIGURED_TASKS as any[])
    .filter(t => !jobId || t.jobId === jobId)
    .map(fromConfiguredTask)

  return [...drawingTasks, ...wiTasks, ...aiTasksList, ...configured]
}

/** Assignable tasks for a job — only approved ones (for crew push). */
export async function getAssignableTasks(jobId: string): Promise<AssignableTask[]> {
  // Phase 6: GET /api/tasks/assignable?jobId=...&approvedOnly=true
  const all = await getAllAssignableTasks(jobId)
  return all.filter(t => t.approvalStatus === 'APPROVED')
}

/** PM approves a DRAFT AI task — makes it visible to crew. */
export async function approveAITask(
  taskId: string,
  approverId: string,
  approverName: string,
): Promise<boolean> {
  // Phase 6: PATCH /api/tasks/:id/approve
  _aiTaskStatuses[taskId] = 'APPROVED'
  _aiApprovers[taskId] = { id: approverId, name: approverName, at: new Date().toISOString() }
  return Promise.resolve(true)
}

/** PM rejects an AI task — removes it from assignable list. */
export async function rejectAITask(taskId: string): Promise<boolean> {
  // Phase 6: PATCH /api/tasks/:id/reject
  _aiTaskStatuses[taskId] = 'REJECTED'
  return Promise.resolve(true)
}

/** Assign a task to crew — stores assignment in mock session. */
export async function assignTask(payload: {
  taskId:       string
  jobId:        string
  assignToIds:  string[]
  assignToNames:string[]
  targetDate:   string
  shiftType:    'DAY' | 'NIGHT'
  priority:     string
  noGoOverrideReason?: string
}): Promise<boolean> {
  // Phase 6: POST /api/tasks/assign
  if (payload.noGoOverrideReason) {
    _noGoOverrides[payload.taskId] = payload.noGoOverrideReason
  }
  console.info('[taskService] assignTask (mock)', payload)
  return Promise.resolve(true)
}

/** Record a NO GO override with a permanent mandatory reason. */
export async function recordNoGoOverride(
  taskId: string,
  reason: string,
): Promise<boolean> {
  // Phase 6: POST /api/tasks/:id/no-go-override
  if (!reason.trim()) return false
  _noGoOverrides[taskId] = reason.trim()
  return Promise.resolve(true)
}

/**
 * 3C-05 — Record WI step completion from crew mobile app.
 * Fires ITPStepCompleted notification via the notification bus.
 * At beta: PATCH /api/wi-steps/:id/complete also writes to the WI record.
 */
export async function recordStepCompletion(
  stepTaskId: string,
  employeeId: string,
  employeeName: string,
  pmEmployeeId: string,
): Promise<void> {
  // Phase 6: PATCH /api/tasks/:id/complete (writes back to WI step record)
  const step = MOCK_WI_STEP_TASKS.find(s => s.id === stepTaskId)
  if (!step) return

  // Fire ITP hold point release notification if this step has a hold point
  if (step.holdPointRef) {
    addCrewNotification({
      type:          'ITP_RELEASE',
      priority:      'HIGH',
      employeeIds:   [pmEmployeeId],
      jobIds:        [step.jobId],
      heading:       `Hold point passed — ${step.holdPointRef}`,
      body:          `${employeeName} has completed "${step.stepTitle}" (${step.wiRef} Step ${step.stepNumber}). Hold point ${step.holdPointRef} is ready for PM witness/sign-off.`,
      requiredAction:'Review the completed step and sign off the hold point in WHS → ITP.',
      sourceModule:  'taskService (WI step completion)',
      sourceRecordId: stepTaskId,
      requiresAck:   true,
      payload: {
        holdPointRef:  step.holdPointRef,
        holdPointDescription: `${step.wiTitle} — ${step.stepTitle}`,
        activityReleased: step.stepDescription.slice(0, 100),
        releasedBy:    `${employeeName} (crew)`,
        releasedAt:    new Date().toISOString(),
      } as ITPReleasePayload,
    })
  } else {
    // No hold point — just notify PM of task completion for review
    addCrewNotification({
      type:          'ITP_RELEASE',
      priority:      'MEDIUM',
      employeeIds:   [pmEmployeeId],
      jobIds:        [step.jobId],
      heading:       `WI step complete — ${step.wiRef} Step ${step.stepNumber}`,
      body:          `${employeeName} has completed "${step.stepTitle}". Photos and sign-off submitted.`,
      sourceModule:  'taskService (WI step completion)',
      sourceRecordId: stepTaskId,
      requiresAck:   false,
      payload: {
        holdPointRef:       step.holdPointRef ?? '',
        holdPointDescription: step.stepTitle,
        activityReleased:   step.stepDescription.slice(0, 100),
        releasedBy:         `${employeeName} (crew)`,
        releasedAt:         new Date().toISOString(),
      } as ITPReleasePayload,
    })
  }
}

/** Task submission records from field. */
export async function getTaskSubmissions(jobId?: string): Promise<MobileTaskRecord[]> {
  // Phase 6: GET /api/tasks/submissions?jobId=...
  const records = jobId
    ? MOCK_MOBILE_TASK_RECORDS.filter(r => r.jobId === jobId)
    : MOCK_MOBILE_TASK_RECORDS
  return Promise.resolve(records)
}

/** Photo submissions, optionally filtered by job. */
export async function getPhotoSubmissions(jobId?: string): Promise<MobilePhoto[]> {
  // Phase 6: GET /api/tasks/photos?jobId=...
  const photos = jobId
    ? MOCK_MOBILE_PHOTOS.filter(p => p.jobId === jobId)
    : MOCK_MOBILE_PHOTOS
  return Promise.resolve(
    photos.map(p => ({
      ...p,
      reviewStatus: _photoReviews[p.id]?.status ?? p.reviewStatus,
      reworkNote:   _photoReviews[p.id]?.note ?? p.reworkNote,
    }))
  )
}

/** Approve a photo submission. */
export async function approvePhoto(photoId: string, reviewedBy: string): Promise<boolean> {
  // Phase 6: PATCH /api/tasks/photos/:id/approve
  _photoReviews[photoId] = { status: 'ACCEPTED' }
  return Promise.resolve(true)
}

/** Reject a photo — dispatches push notification to submitting crew member. */
export async function rejectPhoto(
  photoId: string,
  rejectionReason: string,
  rejectedBy: string,
  submittingEmployeeId: string,
  submittingEmployeeName: string,
  taskTitle: string,
): Promise<boolean> {
  // Phase 6: PATCH /api/tasks/photos/:id/reject
  _photoReviews[photoId] = { status: 'FLAGGED_REWORK', note: rejectionReason }

  addCrewNotification({
    type:          'CUSTOM_WHSO_ALERT',
    priority:      'MEDIUM',
    employeeIds:   [submittingEmployeeId],
    jobIds:        [],
    heading:       `Photo rejected — ${taskTitle}`,
    body:          `Your photo for "${taskTitle}" was rejected by ${rejectedBy}. Reason: ${rejectionReason}`,
    requiredAction:'Re-submit a clear, in-focus photo of the completed work.',
    sourceModule:  'taskService',
    sourceRecordId: photoId,
    requiresAck:   false,
    payload:       { photoId, rejectionReason, rejectedBy },
  })

  return Promise.resolve(true)
}

// Re-export raw mock data for components that need direct access
export { MOCK_DRAWING_TASKS, MOCK_WI_STEP_TASKS, MOCK_AI_TASKS }
